import { useState, useEffect, lazy, Suspense, startTransition, type ComponentType } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
// Index is the landing route — keep eager so first paint has no extra chunk hop.
import Index from "./pages/Index";
import LoadingScreen from "./components/LoadingScreen";
import IntroVideoGate from "./components/IntroVideoGate";

import RouteLogger from "./components/RouteLogger";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { PlayingContext } from "./contexts/PlayingContext";
import { HeartbeatContext } from "./contexts/HeartbeatContext";

import { useBpmPulse } from "./hooks/useBpmPulse";

type LazyModule<T extends ComponentType<any>> = { default: T };
type LazyLoader<T extends ComponentType<any>> = () => Promise<LazyModule<T>>;
const NullComponent = () => null;

const MODULE_IMPORT_ERROR_PATTERNS = [
  "Importing a module script failed",
  "Failed to fetch dynamically imported module",
  "Loading chunk",
  "ChunkLoadError",
];

const isModuleImportError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return MODULE_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const lazyWithRetry = <T extends ComponentType<any>>(
  loader: LazyLoader<T>,
  options: { optional?: boolean } = {}
) =>
  lazy(async () => {
    try {
      return await loader();
    } catch (error) {
      if (isModuleImportError(error)) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        try {
          return await loader();
        } catch (retryError) {
          if (options.optional && isModuleImportError(retryError)) {
            return { default: NullComponent as unknown as T };
          }
          throw retryError;
        }
      }
      throw error;
    }
  });

// Code-split secondary routes so they don't bloat the initial JS bundle.
// Each becomes its own chunk, fetched only when the user navigates to it.
const loadDedicate = () => import("./pages/Dedicate");
const loadHowItWorks = () => import("./pages/HowItWorks");
const loadUkhozi = () => import("./pages/UkhoziPage");
const loadAdmin = () => import("./pages/Admin");
const loadNotFound = () => import("./pages/NotFound");

const DevQueueSimulator = import.meta.env.DEV
  ? lazyWithRetry(() => import("./components/QueueSimulator"), { optional: true })
  : null;
const Dedicate = lazyWithRetry(loadDedicate);
const HowItWorks = lazyWithRetry(loadHowItWorks);
const UkhoziPage = lazyWithRetry(loadUkhozi);
const Admin = lazyWithRetry(loadAdmin);
const NotFound = lazyWithRetry(loadNotFound);

/**
 * Warm secondary-route chunks once the intro video is done.
 * Uses requestIdleCallback so the prefetch never contends with the
 * listen page's first meaningful paint or the tracks fetch.
 */
let __routesPrefetched = false;
function prefetchSecondaryRoutes() {
  if (__routesPrefetched) return;
  __routesPrefetched = true;
  const run = () => {
    // Fire and forget — errors are handled by lazyWithRetry on real navigation.
    void loadDedicate().catch(() => {});
    void loadHowItWorks().catch(() => {});
    void loadUkhozi().catch(() => {});
  };
  const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 2500 });
  } else {
    window.setTimeout(run, 800);
  }
}

const queryClient = new QueryClient();

// Session-scoped flag — once the loading sequence has played in this tab,
// subsequent route changes (or remounts) skip it. Cleared when the tab closes.
// Loading bar + intro video play on EVERY visit — no session persistence.
const App = () => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [introDone, setIntroDone] = useState<boolean>(false);
  const handleLoadComplete = () => startTransition(() => setLoaded(true));
  const handleIntroComplete = () => startTransition(() => setIntroDone(true));
  // Global playing flag — set by Index.tsx playback engine, read by any page

  const [globalPlaying, setGlobalPlaying] = useState(false);

  // Single shared heartbeat instance for the whole app
  const heartbeat = useBpmPulse();

  const ready = loaded && introDone;

  // Warm secondary-route JS chunks as soon as the intro finishes so the
  // first navigation to /dedicate, /how-it-works, or /ukhozi feels instant.
  useEffect(() => {
    if (introDone) prefetchSecondaryRoutes();
  }, [introDone]);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Toaster />
          <Sonner />
          <TooltipProvider>
            <HeartbeatContext.Provider value={heartbeat}>
              <PlayingContext.Provider value={globalPlaying}>
                {!loaded && <LoadingScreen onComplete={handleLoadComplete} />}
                {loaded && !introDone && <IntroVideoGate onComplete={handleIntroComplete} />}
                <div className={`transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0"}`}>
                  <BrowserRouter>
                    <RouteLogger />
                    <Suspense fallback={null}>
                      {DevQueueSimulator && <DevQueueSimulator />}
                      <Routes>
                        <Route path="/" element={<Index onPlayingChange={setGlobalPlaying} pageReady={ready} />} />
                        <Route path="/dedicate" element={<Dedicate />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/ukhozi" element={<UkhoziPage />} />
                        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </BrowserRouter>
                </div>
              </PlayingContext.Provider>
            </HeartbeatContext.Provider>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
